import React, { useState, useEffect } from 'react';
import { simulationsApi, leadsApi } from '../services/api';
import { useProduct } from '../context/ProductContext';
import {
  Play,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  SkipForward,
  Beaker,
  Zap,
  History,
  Eye,
  ChevronRight,
  Cpu,
  ShieldCheck,
  GitBranch,
  Mail,
  Wallet as WalletIcon,
  FileText,
  Activity,
  Gauge,
  Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';

const STEP_ICON_MAP = {
  trigger_fired: Zap,
  consent_check: ShieldCheck,
  guidance_match: GitBranch,
  ai_generation: Cpu,
  queue_decision: Layers,
  queue_for_approval: Layers,
  sandbox_send: Beaker,
  real_send: Mail,
  wallet_transaction: WalletIcon,
  wallet_rollback: WalletIcon,
  usage_log: FileText,
  journey_timeline: Activity,
  dashboard_metrics: Gauge,
  runtime_stats: Gauge
};

const STATUS_STYLE = {
  success: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', Icon: CheckCircle2 },
  failed: { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', Icon: XCircle },
  skipped: { color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', Icon: SkipForward },
  pending: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', Icon: Clock }
};

const prettyStep = (name) =>
  String(name || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function SimulationRunner() {
  const { selectedProduct } = useProduct();
  const [actionTypes, setActionTypes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const [formLeadId, setFormLeadId] = useState('');
  const [formActionType, setFormActionType] = useState('scheduling');
  const [formNote, setFormNote] = useState('');

  const [activeTrace, setActiveTrace] = useState(null);
  const [traceOpen, setTraceOpen] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProduct && selectedProduct !== 'all') params.product_id = selectedProduct;
      const [atRes, leadsRes, simsRes] = await Promise.all([
        simulationsApi.actionTypes(),
        leadsApi.list({ limit: 100 }),
        simulationsApi.list({ ...params, limit: 25, only_simulations: true })
      ]);
      setActionTypes(atRes.data || []);
      setLeads(leadsRes.data || []);
      setSimulations(simsRes.data.items || []);
      if (!formLeadId && (leadsRes.data || []).length > 0) {
        setFormLeadId(leadsRes.data[0].id);
      }
    } catch (e) {
      toast.error('Failed to load simulation data');
    } finally {
      setLoading(false);
    }
  };

  const handleRun = async () => {
    if (!formLeadId || !formActionType) {
      toast.error('Pick a lead and action type first');
      return;
    }
    setRunning(true);
    try {
      const res = await simulationsApi.run({
        lead_id: formLeadId,
        action_type: formActionType,
        note: formNote || undefined
      });
      toast.success(`Simulation ${res.data.final_status} — action_id ${res.data.action_id.slice(0, 8)}`);
      setActiveTrace(res.data);
      setTraceOpen(true);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Simulation failed');
    } finally {
      setRunning(false);
    }
  };

  const handleView = async (action_id) => {
    try {
      const res = await simulationsApi.get(action_id);
      setActiveTrace(res.data);
      setTraceOpen(true);
    } catch (e) {
      toast.error('Could not load trace');
    }
  };

  const handleReplay = async (action_id) => {
    setRunning(true);
    try {
      const res = await simulationsApi.replay(action_id);
      toast.success('Replayed simulation');
      setActiveTrace(res.data);
      setTraceOpen(true);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Replay failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-72"></div>
          <div className="h-64 bg-slate-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="simulation-runner-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Beaker className="w-7 h-7 text-blue-600" />
            Sandbox Simulation Runner
          </h1>
          <p className="text-slate-500 mt-1">
            Safely run the full AI workflow in <span className="font-medium">sandbox mode</span>{' '}
            — no real email/SMS, no credit deduction, full lifecycle trace.
          </p>
        </div>
        <Button onClick={loadAll} variant="outline" className="gap-2" data-testid="refresh-sims-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Run Form */}
      <Card className="mb-6 border-blue-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Run Sandbox Simulation
          </CardTitle>
          <CardDescription>
            Pick a lead + action type. The simulation will execute the full unified lifecycle
            (Trigger → Consent → Guidance → AI Generation → Queue → Sandbox Send → Wallet → Usage → Timeline → Metrics).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase text-slate-500">Lead</Label>
              <Select value={formLeadId} onValueChange={setFormLeadId}>
                <SelectTrigger data-testid="sim-lead-select" className="mt-1">
                  <SelectValue placeholder="Select a lead" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} — {l.company || l.email}
                      {l.product_name ? ` · ${l.product_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Action Type</Label>
              <Select value={formActionType} onValueChange={setFormActionType}>
                <SelectTrigger data-testid="sim-action-select" className="mt-1">
                  <SelectValue placeholder="Action type" />
                </SelectTrigger>
                <SelectContent>
                  {actionTypes.map(at => (
                    <SelectItem key={at.action_type} value={at.action_type}>
                      {prettyStep(at.action_type)} · {at.estimated_credits} cr
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs uppercase text-slate-500">Note / Context (optional)</Label>
              <Input
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
                placeholder="e.g. Tested by QA on Mar 1"
                data-testid="sim-note-input"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Beaker className="w-4 h-4 text-blue-500" />
              Sandbox is <strong>forced</strong>: zero real sends, zero credits charged.
            </div>
            <Button
              onClick={handleRun}
              disabled={running || !formLeadId}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="sim-run-btn"
            >
              <Play className="w-4 h-4" />
              {running ? 'Running…' : 'Run Sandbox Simulation'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Simulations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Recent Simulations
          </CardTitle>
          <CardDescription>Latest sandbox runs (filtered by selected product).</CardDescription>
        </CardHeader>
        <CardContent>
          {simulations.length === 0 ? (
            <div className="py-10 text-center text-slate-500" data-testid="no-sims-empty">
              No simulations yet — run one above to see the lifecycle.
            </div>
          ) : (
            <div className="space-y-2">
              {simulations.map(s => {
                const style = STATUS_STYLE[
                  s.final_status === 'simulated' ? 'success'
                    : s.final_status === 'failed' ? 'failed'
                    : s.final_status === 'skipped' ? 'skipped'
                    : 'pending'
                ];
                return (
                  <div
                    key={s.action_id}
                    data-testid={`sim-row-${s.action_id}`}
                    className={`flex items-center justify-between p-3 rounded-lg border ${style.border} ${style.bg}`}
                  >
                    <div className="flex items-center gap-3">
                      <style.Icon className={`w-5 h-5 ${style.color}`} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{s.lead_name || 'Unknown'}</span>
                          <Badge variant="outline" className="capitalize">
                            {prettyStep(s.action_type)}
                          </Badge>
                          {s.agent_type && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 capitalize">
                              {prettyStep(s.agent_type)}
                            </Badge>
                          )}
                          {s.product_name && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                              {s.product_name}
                            </Badge>
                          )}
                          <Badge className={`${style.bg} ${style.color} border ${style.border}`}>
                            {s.final_status}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                          <span>action_id: <code className="text-[10px]">{s.action_id.slice(0, 8)}</code></span>
                          <span>•</span>
                          <span>{fmt(s.started_at)}</span>
                          {s.credits_used > 0 && <span>• {s.credits_used} credits</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm" variant="outline" className="gap-1"
                        onClick={() => handleView(s.action_id)}
                        data-testid={`sim-view-${s.action_id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View Trace
                      </Button>
                      <Button
                        size="sm" variant="outline" className="gap-1"
                        onClick={() => handleReplay(s.action_id)}
                        disabled={running}
                        data-testid={`sim-replay-${s.action_id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Replay
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trace Dialog */}
      <Dialog open={traceOpen} onOpenChange={setTraceOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Lifecycle Trace
            </DialogTitle>
            <DialogDescription>
              Step-by-step trace of the unified AI action lifecycle.
            </DialogDescription>
          </DialogHeader>

          {activeTrace && (
            <div className="space-y-5">
              {/* Header data */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <Stat label="action_id" value={activeTrace.action_id?.slice(0, 12)} mono />
                <Stat label="Status" value={activeTrace.final_status} highlight />
                <Stat label="Sandbox" value={activeTrace.sandbox ? 'YES' : 'NO'} />
                <Stat label="Agent" value={activeTrace.agent_type || '—'} />
                <Stat label="Review Mode" value={activeTrace.review_mode || '—'} />
                <Stat label="Credits Used" value={`${activeTrace.credits_used ?? 0}`} />
                <Stat label="Trigger Event" value={activeTrace.trigger_event || '—'} />
                <Stat label="Subject" value={activeTrace.subject || '—'} />
                <Stat
                  label="Stop Conditions"
                  value={(activeTrace.stop_conditions || []).join(', ') || '—'}
                  wide
                />
              </div>

              {activeTrace.ai_reason && (
                <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <span className="font-medium">AI Reason:</span> {activeTrace.ai_reason}
                </div>
              )}

              {activeTrace.preview && (
                <div className="p-3 rounded bg-slate-50 border border-slate-200 text-sm">
                  <div className="text-xs uppercase text-slate-500 mb-1">Generated Preview</div>
                  <p className="whitespace-pre-wrap text-slate-700">{activeTrace.preview}</p>
                </div>
              )}

              {activeTrace.error_message && (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-900">
                  <span className="font-medium">Error:</span> {activeTrace.error_message}
                </div>
              )}

              {/* Steps */}
              <div>
                <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-2">
                  <ChevronRight className="w-3.5 h-3.5" />
                  Steps ({activeTrace.steps?.length || 0})
                </div>
                <ol className="space-y-2">
                  {(activeTrace.steps || []).map((step, idx) => {
                    const StyleSet = STATUS_STYLE[step.status] || STATUS_STYLE.pending;
                    const StepIcon = STEP_ICON_MAP[step.name] || Layers;
                    return (
                      <li
                        key={`${step.name}-${idx}`}
                        data-testid={`trace-step-${step.name}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${StyleSet.border} ${StyleSet.bg}`}
                      >
                        <div className="flex flex-col items-center pt-0.5">
                          <StepIcon className={`w-4 h-4 ${StyleSet.color}`} />
                          <div className="text-[10px] text-slate-400 mt-1">{idx + 1}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{prettyStep(step.name)}</span>
                            <Badge className={`${StyleSet.bg} ${StyleSet.color} border ${StyleSet.border}`}>
                              {step.status}
                            </Badge>
                            <span className="text-[10px] text-slate-400 ml-auto">{fmt(step.timestamp)}</span>
                          </div>
                          {step.detail && (
                            <div className="text-sm text-slate-600 mt-1">{step.detail}</div>
                          )}
                          {step.data && Object.keys(step.data).length > 0 && (
                            <details className="mt-1">
                              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                                data ({Object.keys(step.data).length})
                              </summary>
                              <pre className="text-[11px] bg-white/50 p-2 rounded mt-1 overflow-x-auto border border-slate-200">
                                {JSON.stringify(step.data, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          )}

          <DialogFooter>
            {activeTrace && (
              <Button
                variant="outline"
                onClick={() => handleReplay(activeTrace.action_id)}
                disabled={running}
                className="gap-2 mr-auto"
                data-testid="trace-replay-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Replay Last Simulation
              </Button>
            )}
            <Button variant="outline" onClick={() => setTraceOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, mono, highlight, wide }) => (
  <div className={`p-2 rounded border border-slate-200 bg-white ${wide ? 'md:col-span-3 col-span-2' : ''}`}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 ${mono ? 'font-mono text-xs' : 'text-sm'} ${highlight ? 'font-semibold text-emerald-700' : 'text-slate-900'}`}>
      {value}
    </div>
  </div>
);
